import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { unsubscribeUser } from "../../../api/api";
import "./Unsubscribe.css";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const [unsubscribeStatus, setUnsubscribeStatus] = useState("idle");
  const [selectedOptions, setSelectedOptions] = useState({
    marketing: false,
    gameUpdates: false,
    newsletters: false,
    allEmails: false,
  });

  const email = searchParams.get("email");
  const token = searchParams.get("token");

  const unsubscribeOptions = [
    {
      id: "marketing",
      label: "Marketing and promotional emails",
      description:
        "Unsubscribe from promotional offers and marketing communications",
    },
    {
      id: "gameUpdates",
      label: "Game notifications and updates",
      description:
        "Stop receiving notifications about game changes, cancellations, and reminders",
    },
    {
      id: "newsletters",
      label: "Newsletter and blog updates",
      description:
        "Unsubscribe from weekly newsletters and blog post notifications",
    },
    {
      id: "allEmails",
      label: "All email communications",
      description:
        "Unsubscribe from all non-essential emails (you will still receive account security emails)",
    },
  ];

  const handleOptionChange = (optionId) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [optionId]: !prev[optionId],
    }));
  };

  const handleUnsubscribe = async () => {
    setUnsubscribeStatus("loading");

    try {
      await unsubscribeUser(email, token, selectedOptions);
      setUnsubscribeStatus("success");
    } catch (error) {
      console.error("Unsubscribe error:", error);
      setUnsubscribeStatus("error");
    }
  };

  const handleResubscribe = () => {
    window.location.href = "/";
  };

  if (unsubscribeStatus === "success") {
    return (
      <div className="success-page">
        <div className="success-card">
          <div className="success-icon">✅</div>
          <h1>Successfully Unsubscribed</h1>
          <p>
            You have been successfully unsubscribed from the selected email
            types.
            {email && ` We have updated the preferences for ${email}.`}
          </p>
          <div className="success-actions">
            <button onClick={handleResubscribe} className="btn-primary">
              Return to GoalFlex
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (unsubscribeStatus === "error") {
    return (
      <div className="error-page">
        <div className="error-card">
          <div className="error-icon">❌</div>
          <h1>Unsubscribe Failed</h1>
          <p>
            We encountered an error while processing your unsubscribe request.
            This might be due to an expired or invalid link.
          </p>
          <div className="error-actions">
            <button
              onClick={() => setUnsubscribeStatus("idle")}
              className="btn-secondary"
            >
              Try Again
            </button>
            <a href="/contact" className="btn-primary">
              Contact Support
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="unsubscribe-container">
      {/* Hero Section */}
      <div className="unsubscribe-hero">
        <div className="container">
          <h1>Manage Email Preferences</h1>
          <p className="unsubscribe-subtitle">
            We are sorry to see you go! You can choose which types of emails you
            would like to unsubscribe from below.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className="unsubscribe-content">
        <div className="container">
          <div className="unsubscribe-card">
            <p className="unsubscribe-intro">
              {email && `Managing preferences for: ${email}`}
            </p>

            <div className="unsubscribe-options">
              {unsubscribeOptions.map((option) => (
                <div key={option.id} className="unsubscribe-option">
                  <label className="option-label">
                    <input
                      type="checkbox"
                      checked={selectedOptions[option.id]}
                      onChange={() => handleOptionChange(option.id)}
                      className="option-checkbox"
                    />
                    <div className="option-content">
                      <div className="option-title">{option.label}</div>
                      <div className="option-description">
                        {option.description}
                      </div>
                    </div>
                  </label>
                </div>
              ))}
            </div>

            <div className="unsubscribe-actions">
              <button
                onClick={handleUnsubscribe}
                disabled={
                  !Object.values(selectedOptions).some(Boolean) ||
                  unsubscribeStatus === "loading"
                }
                className="btn-primary"
              >
                {unsubscribeStatus === "loading"
                  ? "Processing..."
                  : "Unsubscribe Selected"}
              </button>
              <a href="/" className="btn-secondary">
                Keep All Subscriptions
              </a>
            </div>

            <div className="unsubscribe-note">
              <p>
                <strong>Note:</strong> You will continue to receive important
                account-related emails such as password resets, security alerts,
                and billing notifications even if you unsubscribe from all
                marketing communications.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
